/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UserProfile } from '../types';
import { Logger } from './logger';
import * as SharedRegistration from '@shared/services/registrationService';

/**
 * Registration Service
 * Handles user registration, activation token generation, and account status management.
 */

export const generateActivationToken = SharedRegistration.generateActivationToken;
export const getActivationTokenExpiry = SharedRegistration.getActivationTokenExpiry;
export const isActivationTokenValid = SharedRegistration.isActivationTokenValid;

/**
 * Generate activation link
 */
export const getActivationLink = (userId: string, token: string): string => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.constantinalifestyle.one';
    return `${baseUrl}/activate?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`;
};

/**
 * Create user registration profile
 */
export const createRegistrationProfile = (email: string): Partial<UserProfile> => {
    Logger.info('UIX: Creating registration profile', { email });
    return SharedRegistration.createRegistrationProfile(email);
};

/**
 * Confirm user email (first step of activation)
 */
export const confirmUserEmail = (user: UserProfile): UserProfile => {
    Logger.info('UIX: Confirming user email', { email: user.email });
    return SharedRegistration.confirmUserEmail(user);
};

export const enableUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('UIX: Enabling user profile', { email: user.email });
    return SharedRegistration.enableUserProfile(user);
};

/**
 * Deactivate user profile
 */
export const deactivateUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('UIX: Deactivating user profile', { email: user.email });
    return SharedRegistration.deactivateUserProfile(user);
};

/**
 * Delete user profile (soft delete)
 */
export const deleteUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('UIX: Deleting user profile', { email: user.email });
    return SharedRegistration.deleteUserProfile(user);
};

/**
 * Check if user can login (must be activated AND enabled)
 */
export const canUserLogin = SharedRegistration.canUserLogin;

/**
 * Resend activation email (generates new token)
 */
export const regenerateActivationToken = (user: UserProfile): UserProfile => {
    Logger.info('UIX: Regenerating activation token', { email: user.email });
    return SharedRegistration.regenerateActivationToken(user);
};
